"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useTheme } from "@/components/theme-provider";
import type { ThemePreference } from "@/lib/theme";

const options: ThemePreference[] = ["light", "dark", "system"];

type Props = {
  variant?: "select" | "icon";
};

export function ThemeSwitcher({ variant = "select" }: Props) {
  const t = useTranslations("nav");
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (variant === "icon") {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          aria-label={t("theme")}
          title={t("theme")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        >
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
          >
            <rect x="3" y="4" width="18" height="12" rx="1.5" />
            <path d="M8 20h8M12 16v4" />
          </svg>
        </button>
        {open ? (
          <div className="absolute right-0 z-50 mt-1 min-w-[8rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {options.map((value) => (
              <button
                key={value}
                type="button"
                className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  value === preference
                    ? "font-semibold text-[var(--brand-green)]"
                    : ""
                }`}
                onClick={() => {
                  setPreference(value);
                  setOpen(false);
                }}
              >
                {t(`themes.${value}`)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <label className="inline-flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
      <span className="sr-only">{t("theme")}</span>
      <select
        className="rounded-md border border-stone-400 bg-white px-2 py-1 text-stone-900 dark:border-stone-600 dark:bg-stone-950 dark:text-stone-100"
        value={preference}
        onChange={(event) =>
          setPreference(event.target.value as ThemePreference)
        }
        aria-label={t("theme")}
      >
        {options.map((value) => (
          <option key={value} value={value}>
            {t(`themes.${value}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
